import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Card, Button, Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Form, FormikProvider, useFormik } from 'formik';
import { AlertMessage } from '@eposnow/ui-core';
import ActionsContainer from '../../components/ActionsContainer';
import PageHeader from '../../components/pageHeaderComponents/PageHeader';
import MFAForm from './MFAForm';
import mfaValidationSchema from './validationSchema';
import { UserContext } from '../../context/UserContext';
import { hasModule } from '../../helpers/helpers';
import CodeVerificationModal from './CodeVerificationModal';
import SkipStepPopup from '../../components/SkipStepPopup';
import ErrorLoadingData from '../../components/ErrorLoadingData';
import { StepContext } from '../../context/StepContext';
import { ApiContext } from '../../context/ApiContext';
import { MFAConfig, MFAErrors, MFAUpdateBody, VerificationCodeDetails } from '../../types';
import Loader from '../../components/Loader';
import { API_V1_HOSTNAME_KEY } from '../../localStorageKeys';
import usePageTitle from '../../helperHooks/usePageTitle';

const emptyMfaConfig = {
  scopes: [],
  email: '',
  mobileNumber: '',
  channel: 'sms',
  banking: false,
  verificationCode: '',
};

const MFA = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { apiFetch } = useContext(ApiContext);
  const { user, userModules, locale } = useContext(UserContext);
  const { goToNextStep, goToPreviousStep } = useContext(StepContext);
  const [validMobileNumber, setValidMobileNumber] = useState<boolean>(false);
  const [mobileNumberError, setMobileNumberError] = useState<string>('');
  const [verificationCodeModalOpen, setVerificationCodeModalOpen] = useState<boolean>(false);
  const [verificationCodeDetails, setVerificationCodeDetails] =
    useState<VerificationCodeDetails>(undefined);
  const [errors, setErrors] = useState<MFAErrors>({});
  const [errorMessageDisplay, setErrorMessageDisplay] = useState('');
  const [mfaConfig, setMfaConfig] = useState(emptyMfaConfig);
  const [skipStepModalOpen, setSkipStepModalOpen] = useState<boolean>(false);
  const isSmsChannelOnly =
    hasModule(userModules, '_EposStandalonePayments') ||
    hasModule(userModules, '_EposNowPayments') ||
    hasModule(userModules, '_EposNowBalancePlatform');

  usePageTitle('nav.mfa');

  let formik;

  const validLoginChannels = isSmsChannelOnly
    ? [{ value: 'sms', label: t('screens.mfa.loginChannels.sms') }]
    : [
        { value: 'email', label: t('screens.mfa.loginChannels.email') },
        { value: 'sms', label: t('screens.mfa.loginChannels.sms') },
      ];

  const parseMfaConfigs = (data: MFAConfig) => ({
    ...data,
    email: data?.email || emptyMfaConfig.email,
    mobileNumber: data?.mobileNumber || emptyMfaConfig.mobileNumber,
    verificationCode: data?.verificationCode || emptyMfaConfig.verificationCode,
    channel:
      data?.scopes?.find((scope) => scope.scope === 'login')?.channel === 'none'
        ? emptyMfaConfig.channel
        : data?.scopes?.find((scope) => scope.scope === 'login')?.channel,
    banking: data?.scopes?.find((scope) => scope.scope === 'banking')?.channel === 'sms',
  });

  const {
    refetch: fetchData,
    data: mfaDataFetched,
    isLoading: isLoadingMFA,
    isError,
  } = useQuery<MFAConfig>(['mfa'], () =>
    apiFetch(`${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/user-mfa-settings`)
  );

  const clearVerificationCode = (clearAndCloseModal = true) => {
    if (!formik) return;

    if (clearAndCloseModal) {
      setVerificationCodeModalOpen(false);
      setVerificationCodeDetails(undefined);
    }
    formik.setValues({
      ...formik.values,
      verificationCode: '',
    });
  };

  const { isLoading: isLoadingUpdate, mutate: updateMfaConfig } = useMutation(
    ({ mfaBody, mfaVerificationCode }: { mfaBody: MFAUpdateBody; mfaVerificationCode: string }) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/user-mfa-settings${
          mfaVerificationCode ? `?verificationCode=${mfaVerificationCode}` : ''
        }`,
        mfaBody,
        'PATCH'
      ),
    {
      async onSuccess() {
        clearVerificationCode(true);
        goToNextStep();
      },
      onError(error: any) {
        if (error?.code === 401) return;
        if (error?.code === 412) {
          // needs a 2FA code and sets the details of the contact info and channel used to send it
          setVerificationCodeDetails(error?.errors as VerificationCodeDetails);
          setVerificationCodeModalOpen(true);
          return;
        }

        let errorMessage = error.message;
        const errorInVerificationCode =
          (error?.code === 422 &&
            error?.errors?.general?.key === 'errorVerificationCodeIncorrect') ||
          (error?.code === 420 && error?.errors?.general?.key === 'errorVerificationCodeLocked') ||
          (error?.code === 410 && error?.errors?.general?.key === 'errorVerificationCodeExpired');

        if (errorInVerificationCode) {
          if (error.code === 422) {
            const remainingAttemptsError =
              error.errors.general?.params && error.errors.general?.params?.length === 2
                ? t(`errors.${error.errors.general.key}`, error.errors.general.message, {
                    remaining: error.errors.general.params[0],
                    total: error.errors.general?.params?.[1],
                  })
                : t(error.errors.general.message);

            setErrors({
              ...errors,
              verificationCodeAlertError: remainingAttemptsError,
            });
            clearVerificationCode(false);
          } else {
            errorMessage = t(`errors.${error.errors.general.key}`, error.errors.general.message);
            clearVerificationCode(true);
            setErrorMessageDisplay(errorMessage);
          }

          return;
        }

        // if (!skipSentry(error)) {
        //   Sentry.captureException(new Error('Error updating MFA settings'), {
        //     extra: { error: JSON.stringify(error) },
        //   });
        // }

        if (error.errors?.general) {
          const generalMessageParams = error.errors.general.params?.reduce(
            (acc: Record<string, string>, item: string, index: number) => ({
              ...acc,
              [`val${index}`]: t(
                `screens.mfa.${item.charAt(0).toLowerCase() + item.slice(1)}`,
                item
              ),
            }),
            {}
          );
          errorMessage = t(
            `errors.${error.errors.general.key}`,
            error.errors.general.message,
            generalMessageParams
          );
        }

        setErrorMessageDisplay(errorMessage);

        if (error.errors?.fields) {
          const errorFields = error.errors.fields;
          Object.keys(errorFields).forEach((key) => {
            const err = errorFields[key];
            let message = '';
            err.forEach((element: { key: string; message: string; params?: string[] }) => {
              const errorParams = element?.params?.reduce(
                (acc, item, index) => ({
                  ...acc,
                  [`val${index}`]: t(
                    `screens.mfa.${item.charAt(0).toLowerCase() + item.slice(1)}`,
                    item
                  ),
                }),
                {}
              );
              message += ` ${t(`errors.${element.key}`, element.message, errorParams)}`;
            });

            errorFields[key] = message;
          });
          setErrors(errorFields);
        }
      },
    }
  );

  formik = useFormik({
    initialValues: {
      ...mfaConfig,
    },
    validationSchema: mfaValidationSchema(verificationCodeModalOpen),
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (isLoadingUpdate) return;
      if (!validMobileNumber && values.mobileNumber !== '') {
        setMobileNumberError('');
        return;
      }

      actions.setSubmitting(false);

      const scopes = [...mfaDataFetched.scopes];

      const login = scopes.findIndex((el) => el.scope === 'login');
      scopes[login] = { ...scopes[login], channel: values.channel };

      if (isSmsChannelOnly) {
        const banking = scopes.findIndex((el) => el.scope === 'banking');
        scopes[banking] = { ...scopes[banking], channel: values.channel };
      }

      const formattedMobileNumber = values.mobileNumber
        .replace(/-/g, '')
        .replace(/\(/g, '')
        .replace(/\)/g, '');

      const requestValues = mfaValidationSchema(verificationCodeModalOpen).cast({
        email: values.email,
        mobileNumber: formattedMobileNumber,
        scopes,
        verificationCode: values.verificationCode,
      });

      const { verificationCode: formattedVerificationCode, ...formattedRequestValues } =
        requestValues;

      updateMfaConfig({
        mfaBody: formattedRequestValues,
        mfaVerificationCode: formattedVerificationCode,
      });
    },
  });

  useEffect(() => {
    const parsedMfaData = parseMfaConfigs(mfaDataFetched);

    if (!parsedMfaData) return;

    setValidMobileNumber(true);
    setMfaConfig(parsedMfaData);
    sessionStorage.setItem('mfa', JSON.stringify({ [`${user.id}`]: parsedMfaData }));
  }, [mfaDataFetched]);

  const clearError = (errorKey: string) => {
    setErrors(({ [errorKey as keyof MFAErrors]: _value, ...remaining }) => remaining);
  };

  const closeVerificationCodeModal = () => {
    setVerificationCodeModalOpen(false);
    formik.setValues({
      ...formik.values,
      verificationCode: '',
    });
  };

  return (
    <>
      <PageHeader />

      <SkipStepPopup
        open={skipStepModalOpen}
        popupTitle={t('screens.mfa.skipMFAModal.title')}
        popupDescription={t('screens.mfa.skipMFAModal.description')}
        handleClose={() => setSkipStepModalOpen(false)}
        handleSkip={goToNextStep}
      />

      {errorMessageDisplay && (
        <AlertMessage
          type="error"
          styles={{ marginBottom: 2 }}
          action={() => setErrorMessageDisplay('')}
          iconAlignment="baseline"
          locale={locale}
          theme={theme}
        >
          {errorMessageDisplay}
        </AlertMessage>
      )}

      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <CodeVerificationModal
            open={verificationCodeModalOpen}
            verificationCodeDetails={verificationCodeDetails}
            handleClose={closeVerificationCodeModal}
            handleSubmit={formik.handleSubmit}
            errors={errors}
            clearError={clearError}
            isLoadingUpdate={isLoadingUpdate}
          />
          <Card
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '1.5rem',
              overflow: 'visible',
            }}
          >
            {isLoadingMFA ? (
              <Loader ariaBusy={isLoadingMFA} />
            ) : (
              <>
                {isError && <ErrorLoadingData action={fetchData} />}
                {!isError && (
                  <MFAForm
                    formik={formik}
                    mobileNumberError={mobileNumberError}
                    setMobileNumberError={setMobileNumberError}
                    validMobileNumber={validMobileNumber}
                    setValidMobileNumber={setValidMobileNumber}
                    validLoginChannels={validLoginChannels}
                    isSmsChannelOnly={isSmsChannelOnly}
                  />
                )}
              </>
            )}
          </Card>
          <ActionsContainer>
            <Button
              variant="outlined"
              sx={{
                margin: '0px',
                flexGrow: '0',
                height: '48px',
              }}
              onClick={() => {
                goToPreviousStep();
              }}
            >
              {t('actions.previous')}
            </Button>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
              <Button
                variant="outlined"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                onClick={() => {
                  setSkipStepModalOpen(true);
                }}
              >
                {t('actions.skip')}
              </Button>
              <Button
                variant="contained"
                sx={{
                  margin: '0px',
                  flexGrow: '0',
                  height: '48px',
                }}
                type="submit"
              >
                {t('actions.next')}
              </Button>
            </Box>
          </ActionsContainer>
        </Form>
      </FormikProvider>
    </>
  );
};

export default MFA;
